/**
 * PDFDownload Component
 *
 * Status: [IMPLEMENTED]
 * Task: T2.8.9 - Implement PDF download
 *
 * Button to download the generated PDF resume.
 */

import React, { useState } from 'react';

const PDFDownload = ({ pdfBase64, filename = 'resume.pdf', changesMd = '' }) => {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const downloadFile = (blob, name) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = () => {
    if (!pdfBase64) {
      setError('No PDF available to download');
      return;
    }

    setDownloading(true);
    setError(null);

    try {
      // Download PDF
      const byteCharacters = atob(pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const pdfBlob = new Blob([byteArray], { type: 'application/pdf' });
      downloadFile(pdfBlob, filename);

      // Download MD if available
      if (changesMd) {
        const mdBlob = new Blob([changesMd], { type: 'text/markdown' });
        const mdFilename = filename.replace('.pdf', '-Changes.md');
        setTimeout(() => downloadFile(mdBlob, mdFilename), 500);
      }
    } catch (err) {
      console.error('Download failed:', err);
      setError('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="mt-6 flex flex-col items-center">
      <button
        onClick={handleDownload}
        disabled={!pdfBase64 || downloading}
        className="flex items-center gap-3 px-8 py-4 bg-sky-600 hover:bg-sky-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors shadow-lg"
      >
        {downloading ? (
          <>
            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Downloading...</span>
          </>
        ) : (
          <>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span>Download PDF/MD</span>
          </>
        )}
      </button>

      {/* Filename hint */}
      {pdfBase64 && !downloading && (
        <p className="mt-2 text-sm text-slate-500">
          {filename}
        </p>
      )}

      {/* Error message */}
      {error && (
        <p className="mt-2 text-sm text-red-400">
          {error}
        </p>
      )}
    </div>
  );
};

export default PDFDownload;
